import type { CognitoUser } from 'amazon-cognito-identity-js'
import * as cognitoAuth from '@/auth/cognito-direct-auth'
import { storeDirectSessionAndNavigate } from '@/auth/direct-session-bridge'
import { takeIntendedRoute } from '@/lib/intended-route'
import type { Step } from './LoginSteps'

export interface LoginFlowState {
	email: string
	password: string
	newPassword: string
	pendingCognitoUser: CognitoUser | null
	resetEmail: string
	resetCode: string
	resetNewPassword: string
	setStep: (step: Step) => void
	setError: (error: string | null) => void
	setInfo: (info: string | null) => void
	setBusy: (busy: boolean) => void
	setEmail: (email: string) => void
	setPassword: (password: string) => void
	setPendingCognitoUser: (user: CognitoUser | null) => void
	setResetCode: (code: string) => void
	setResetNewPassword: (password: string) => void
}

async function withBusyState(state: LoginFlowState, run: () => Promise<void>) {
	state.setError(null)
	state.setInfo(null)
	state.setBusy(true)
	try {
		await run()
	} catch (err) {
		state.setError(cognitoAuth.describeCognitoError(err))
	} finally {
		state.setBusy(false)
	}
}

export const signIn = (state: LoginFlowState) =>
	withBusyState(state, async () => {
		const result = await cognitoAuth.signIn(state.email, state.password)
		if (result.kind === 'new_password_required') {
			state.setPendingCognitoUser(result.cognitoUser)
			state.setStep('newPassword')
			return
		}
		await storeDirectSessionAndNavigate(result.tokens, takeIntendedRoute())
	})

export const completeNewPassword = (state: LoginFlowState) =>
	withBusyState(state, async () => {
		if (!state.pendingCognitoUser) return
		const tokens = await cognitoAuth.completeNewPassword(state.pendingCognitoUser, state.newPassword)
		await storeDirectSessionAndNavigate(tokens, takeIntendedRoute())
	})

export const forgotPassword = (state: LoginFlowState) =>
	withBusyState(state, async () => {
		await cognitoAuth.forgotPassword(state.resetEmail)
		state.setStep('forgotConfirm')
	})

export const confirmForgotPassword = (state: LoginFlowState) =>
	withBusyState(state, async () => {
		await cognitoAuth.confirmForgotPassword(state.resetEmail, state.resetCode, state.resetNewPassword)
		state.setEmail(state.resetEmail)
		state.setPassword('')
		state.setResetCode('')
		state.setResetNewPassword('')
		state.setInfo('Password updated — sign in with your new password.')
		state.setStep('email')
	})
